import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const RevenueTrendChart = ({ data, loading }) => {
  const { t, currentLanguage } = useLanguage();

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg shadow-warm-md p-3">
          <p className="text-sm text-primary font-semibold data-text">
            {t('analytics.revenue')}: {payload?.[0]?.value?.toLocaleString()} MAD
          </p>
          {payload?.[1] && (
            <p className="text-sm text-success font-semibold data-text">
              {t('analytics.pilgrims')}: {payload?.[1]?.value}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm border border-border animate-pulse">
        <div className="h-6 bg-muted rounded w-48 mb-6"></div>
        <div className="h-80 bg-muted rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-3 sm:p-4 md:p-6 shadow-warm border border-border">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <h3 className="text-base sm:text-lg md:text-xl font-semibold text-foreground">
          {t('analytics.revenueTrend')}
        </h3>
        <div className="flex flex-wrap items-center gap-3 md:gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <span className="text-sm text-muted-foreground">
              {t('analytics.revenue')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-success" />
            <span className="text-sm text-muted-foreground">
              {t('analytics.pilgrims')}
            </span>
          </div>
        </div>
      </div>
      <div className="w-full h-64 sm:h-72 md:h-80" aria-label={t('analytics.revenueTrend')}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              yAxisId="left"
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => `${(value / 1000)?.toFixed(0)}k`}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="revenue" 
              stroke="var(--color-primary)" 
              strokeWidth={2}
              dot={{ fill: 'var(--color-primary)', r: 4 }}
              name={t('analytics.revenue')}
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="pilgrims" 
              stroke="var(--color-success)" 
              strokeWidth={2}
              dot={{ fill: 'var(--color-success)', r: 3 }}
              name={t('analytics.pilgrims')}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RevenueTrendChart;